import React, { useState, useCallback, useEffect } from 'react';
import type { CapacitySpan } from '@interlucent/organization-protocol';
import { Modal, FormField } from '../shared';
import { USER_TIMEZONE, TIMEZONES } from './CapacityScheduleEditor';
import styles from './ScheduleSpanModal.module.css';

export interface ScheduleSpanModalProps {
  /**
   * Controls modal visibility
   */
  isOpen: boolean;

  /**
   * Span being edited (undefined for new span)
   */
  span?: CapacitySpan;

  /**
   * Initial values for new span creation
   */
  initialValues?: {
    dayOfWeek?: number;
    startHour?: number;
    endHour?: number;
  };

  /**
   * Callback when modal closes
   */
  onClose: () => void;

  /**
   * Callback when span is saved
   */
  onSave: (span: Omit<CapacitySpan, 'id'>) => void;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

/**
 * ScheduleSpanModal Component
 *
 * Modal for adding or editing a capacity schedule span.
 *
 * Features:
 * - Schedule type toggle (Recurring weekly / One-time)
 * - Day-of-week checkboxes for recurring schedules
 * - Start/End time inputs
 * - Expected sessions input
 * - Description field
 * - Form validation
 */
export const ScheduleSpanModal: React.FC<ScheduleSpanModalProps> = ({
  isOpen,
  span,
  initialValues,
  onClose,
  onSave,
}) => {
  const isEditing = Boolean(span);

  // Default one-time dates: today 09:00 → today 17:00 (in user TZ).
  const todayLocal = (() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  })();

  // Form state
  const [type, setType] = useState<'recurring' | 'one_time'>('recurring');
  const [timezone, setTimezone] = useState(USER_TIMEZONE || 'UTC');
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1, 2, 3, 4, 5]); // Mon-Fri default
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  // One-time spans: ISO-ish wall-clock in `timezone` (datetime-local input shape).
  const [startDate, setStartDate] = useState<string>(`${todayLocal}T09:00`);
  const [endDate, setEndDate] = useState<string>(`${todayLocal}T17:00`);
  // All-day toggle: when true, both startTime and endTime are pinned to
  // "00:00" and the backend treats equal times as a 24h continuous span.
  const [allDay, setAllDay] = useState(false);
  const [floor, setFloor] = useState<number | undefined>(undefined);
  const [ceiling, setCeiling] = useState<number | undefined>(5);
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form from span or initial values
  useEffect(() => {
    if (isOpen) {
      if (span) {
        // Editing existing span
        setType(span.type);
        setTimezone(span.timezone || USER_TIMEZONE || 'UTC');
        setDaysOfWeek(span.daysOfWeek || []);
        setStartTime(span.startTime || '09:00');
        setEndTime(span.endTime || '17:00');
        // datetime-local expects "YYYY-MM-DDTHH:mm" — strip seconds/offset if present.
        const toLocal = (s?: string): string =>
          s ? s.replace(/(Z|[+-]\d{2}:?\d{2})$/, '').slice(0, 16) : `${todayLocal}T09:00`;
        setStartDate(toLocal(span.startDate));
        setEndDate(toLocal(span.endDate));
        // Detect "all day" from the equal-times convention. The legacy
        // 22:00→21:59 workaround is NOT detected as all-day on purpose —
        // operators should re-save those spans to migrate them.
        setAllDay(
          !!span.startTime &&
          !!span.endTime &&
          span.startTime === span.endTime,
        );
        setFloor(span.floor);
        setCeiling(span.ceiling ?? span.expectedSessions);
        setDescription(span.description || '');
      } else if (initialValues) {
        // Creating new span with initial values
        setType('recurring');
        setDaysOfWeek(
          initialValues.dayOfWeek !== undefined ? [initialValues.dayOfWeek] : [1, 2, 3, 4, 5]
        );
        setStartTime(
          initialValues.startHour !== undefined
            ? `${String(initialValues.startHour).padStart(2, '0')}:00`
            : '09:00'
        );
        setEndTime(
          initialValues.endHour !== undefined
            ? `${String(initialValues.endHour).padStart(2, '0')}:00`
            : '17:00'
        );
        setAllDay(false);
        setTimezone(USER_TIMEZONE || 'UTC');
        setFloor(undefined);
        setCeiling(5);
        setDescription('');
      } else {
        // Creating new span with defaults
        setType('recurring');
        setTimezone(USER_TIMEZONE || 'UTC');
        setDaysOfWeek([1, 2, 3, 4, 5]);
        setStartTime('09:00');
        setEndTime('17:00');
        setAllDay(false);
        setFloor(undefined);
        setCeiling(5);
        setDescription('');
      }
      setErrors({});
    }
  }, [isOpen, span, initialValues]);

  const handleDayToggle = useCallback((day: number) => {
    setDaysOfWeek((prev) => {
      if (prev.includes(day)) {
        return prev.filter((d) => d !== day);
      } else {
        return [...prev, day].sort((a, b) => a - b);
      }
    });
  }, []);

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (type === 'recurring') {
      if (daysOfWeek.length === 0) {
        newErrors.daysOfWeek = 'Select at least one day';
      }
      if (!startTime) {
        newErrors.startTime = 'Start time is required';
      }
      if (!endTime) {
        newErrors.endTime = 'End time is required';
      }
      // Equal start/end times are intentionally allowed: they encode "all
      // day" — a 24h continuous span starting at `startTime`. The backend
      // converts this to durationMinutes = 1440. The "All day" toggle below
      // is the friendly way to set this.
    } else {
      // one_time
      if (!startDate) newErrors.startDate = 'Start date/time is required';
      if (!endDate) newErrors.endDate = 'End date/time is required';
      if (startDate && endDate && endDate <= startDate) {
        newErrors.endDate = 'End must be after start';
      }
    }

    if (floor === undefined && ceiling === undefined) {
      newErrors.bounds = 'At least one of floor or ceiling must be set';
    }
    if (floor !== undefined && floor < 0) {
      newErrors.floor = 'Floor must be >= 0';
    }
    if (ceiling !== undefined && ceiling < 0) {
      newErrors.ceiling = 'Ceiling must be >= 0';
    }
    if (floor !== undefined && ceiling !== undefined && floor > ceiling) {
      newErrors.floor = 'Floor must be <= ceiling';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [type, daysOfWeek, startTime, endTime, startDate, endDate, floor, ceiling]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!validate()) return;

      const newSpan: Omit<CapacitySpan, 'id'> = {
        type,
        timezone,
        floor,
        ceiling,
        description: description || undefined,
      };

      if (type === 'recurring') {
        newSpan.daysOfWeek = daysOfWeek;
        newSpan.startTime = startTime;
        newSpan.endTime = endTime;
      } else {
        // one_time — datetime-local values are naive wall-clock in `timezone`.
        // The scheduler interprets them in the span's timezone.
        newSpan.startDate = startDate;
        newSpan.endDate = endDate;
      }

      onSave(newSpan);
    },
    [validate, type, timezone, daysOfWeek, startTime, endTime, startDate, endDate, floor, ceiling, description, onSave]
  );

  return (
    <Modal
      isOpen={isOpen}
      title={isEditing ? 'Edit Capacity Span' : 'Add Capacity Span'}
      onClose={onClose}
      size="medium"
    >
      <form
        onSubmit={handleSubmit}
        onKeyDown={(e) => e.key === 'Enter' && e.stopPropagation()}
        className={styles.form}
      >
        {/* Schedule Type */}
        <FormField label="Schedule Type" htmlFor="span-type">
          <div className={styles.typeToggle}>
            <button
              type="button"
              className={`${styles.typeButton} ${type === 'recurring' ? styles.typeButtonActive : ''}`}
              onClick={() => setType('recurring')}
            >
              Recurring Weekly
            </button>
            <button
              type="button"
              className={`${styles.typeButton} ${type === 'one_time' ? styles.typeButtonActive : ''}`}
              onClick={() => setType('one_time')}
            >
              One-Time
            </button>
          </div>
        </FormField>

        {/* Timezone */}
        <FormField
          label="Timezone"
          htmlFor="span-timezone"
          helperText="Times are evaluated in this timezone (DST-aware)"
        >
          <select
            id="span-timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            {TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </FormField>

        {/* Recurring schedule fields */}
        {type === 'recurring' && (
          <>
            {/* Days of Week */}
            <FormField
              label="Days of Week"
              htmlFor="span-days"
              required
              error={errors.daysOfWeek}
            >
              <div className={styles.daysGrid}>
                {DAYS_OF_WEEK.map((day) => (
                  <label key={day.value} className={styles.dayCheckbox}>
                    <input
                      type="checkbox"
                      checked={daysOfWeek.includes(day.value)}
                      onChange={() => handleDayToggle(day.value)}
                    />
                    <span className={styles.dayLabel}>{day.label.slice(0, 3)}</span>
                  </label>
                ))}
              </div>
            </FormField>

            {/* Time Range */}
            <div className={styles.timeRow}>
              <FormField
                label="Start Time"
                htmlFor="span-start-time"
                required
                error={errors.startTime}
              >
                <input
                  id="span-start-time"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  disabled={allDay}
                />
              </FormField>

              <FormField label="End Time" htmlFor="span-end-time" required error={errors.endTime}>
                <input
                  id="span-end-time"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  disabled={allDay}
                />
              </FormField>
            </div>

            {/* All-day toggle. When enabled, locks both fields to 00:00 — */}
            {/* the backend converts equal start/end into a 24h continuous span. */}
            <FormField label="" htmlFor="span-all-day">
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
                <input
                  id="span-all-day"
                  type="checkbox"
                  checked={allDay}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setAllDay(checked);
                    if (checked) {
                      setStartTime('00:00');
                      setEndTime('00:00');
                    }
                  }}
                />
                All day (24 hours, no interruption)
              </label>
            </FormField>
          </>
        )}

        {/* One-time schedule fields */}
        {type === 'one_time' && (
          <div className={styles.timeRow}>
            <FormField
              label="Start"
              htmlFor="span-start-date"
              required
              error={errors.startDate}
              helperText="Date and time, interpreted in the selected timezone"
            >
              <input
                id="span-start-date"
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </FormField>
            <FormField
              label="End"
              htmlFor="span-end-date"
              required
              error={errors.endDate}
              helperText="Must be after start"
            >
              <input
                id="span-end-date"
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </FormField>
          </div>
        )}

        {/* Demand Bounds */}
        {errors.bounds && (
          <div className={styles.errorText}>{errors.bounds}</div>
        )}
        <div className={styles.timeRow}>
          <FormField
            label="Floor (minimum sessions)"
            htmlFor="span-floor"
            error={errors.floor}
            helperText="Minimum sessions guaranteed during this window"
          >
            <input
              id="span-floor"
              type="number"
              min="0"
              value={floor ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setFloor(v === '' ? undefined : Math.max(0, parseInt(v) || 0));
              }}
              placeholder="Optional"
              style={{ width: 100 }}
            />
          </FormField>

          <FormField
            label="Ceiling (maximum sessions)"
            htmlFor="span-ceiling"
            error={errors.ceiling}
            helperText="Maximum sessions allowed during this window"
          >
            <input
              id="span-ceiling"
              type="number"
              min="0"
              value={ceiling ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setCeiling(v === '' ? undefined : Math.max(0, parseInt(v) || 0));
              }}
              placeholder="Optional"
              style={{ width: 100 }}
            />
          </FormField>
        </div>

        {/* Description */}
        <FormField label="Description" htmlFor="span-description" helperText="Optional notes">
          <input
            id="span-description"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., Business hours capacity"
          />
        </FormField>

        {/* Actions */}
        <div className={styles.actions}>
          <button type="button" onClick={onClose} className={styles.buttonSecondary}>
            Cancel
          </button>
          <button type="submit" className={styles.buttonPrimary}>
            {isEditing ? 'Save Changes' : 'Add Span'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
